import { deleteObject, getStorage, ref, uploadBytes } from 'firebase/storage';
import { v4 as uuid } from 'uuid';
import api from '../api';

export interface ChatUploadResult {
  fileName: string;
  realFileName: string;
}

export const CHAT_FILE_ACCEPT = '.png,.jpg,.jpeg,.txt,.pdf';
export const MAX_CHAT_FILE_SIZE_BYTES = 5 * 1024 * 1024;

export const isChatImageFile = (fileName: string): boolean => /\.(png|jpe?g)$/i.test(fileName);

const ALLOWED_CHAT_FILE_MIME_TYPES: Record<string, string[]> = {
  png: ['image/png'],
  jpg: ['image/jpeg', 'image/jpg'],
  jpeg: ['image/jpeg', 'image/jpg'],
  txt: ['text/plain'],
  pdf: ['application/pdf'],
};

export const isValidChatFile = (file: File): boolean => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (!extension || file.size > MAX_CHAT_FILE_SIZE_BYTES) {
    return false;
  }

  const allowedMimeTypes = ALLOWED_CHAT_FILE_MIME_TYPES[extension];
  return Boolean(allowedMimeTypes && (!file.type || allowedMimeTypes.includes(file.type.toLowerCase())));
};

export const uploadChatFile = async (
  nursingHomeId: number,
  file: File,
): Promise<ChatUploadResult> => {
  if (!isValidChatFile(file)) {
    throw new Error('Unsupported chat file');
  }

  const fileName = uuid();
  const storageRef = ref(getStorage(), `chats/${nursingHomeId}/${fileName}`);
  await uploadBytes(storageRef, file);

  return {
    fileName,
    realFileName: file.name,
  };
};

export const downloadChatFile = (
  nursingHomeId: number,
  fileName: string,
): Promise<Blob> =>
  api.get(`/nursing-homes/${nursingHomeId}/chats/files/download`, {
    params: { fileName },
    responseType: 'blob',
  });

export const deleteChatFile = (nursingHomeId: number, fileName: string): Promise<void> =>
  deleteObject(ref(getStorage(), `chats/${nursingHomeId}/${fileName}`));
